import React, { useState } from 'react';
import { X, Save, Upload } from 'lucide-react';
import { SEGMENTS, THEMES, TYPES, COLLECTION_LABEL } from '../../lib/catalog';
import { api, API } from './adminApi';

const BLANK = {
  name: '', description: '', story: '', price: 0, original_price: null,
  images: [], category: 'unisex', collection: 'genz', segment: null, theme: null, offer: null, product_type: 'tee',
  sizes: [], colors: [], stock: 50, in_stock: true, featured: false, published: true,
};
const MRP = { 399: 1999, 599: 2999, 799: 3999, 999: 4999, 1999: 7999, 3999: 12999, 5999: 17999, 7999: 21999, 9999: 27999, 15999: 39999 };
const SEG_OFFER = { 999: 'b1g1', 3999: 'b2g1' };
const Sel = ({ label, value, onChange, options, testid }) => (
  <label>{label}
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} data-testid={testid} className="w-full border px-3 py-2 mt-1 bg-white">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const emptyToArr = (v) => Array.isArray(v) ? v : String(v||'').split(',').map((x)=>x.trim()).filter(Boolean);

export const ProductEditor = ({ existing, onClose, onSaved }) => {
  const [p, setP] = useState(existing || BLANK);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const r = await api.upload(file);
      if (r.url) setP((prev) => ({ ...prev, images: [...(prev.images||[]), r.url] }));
    } finally { setUploading(false); e.target.value = ''; }
  };
  const save = async () => {
    setErr('');
    try {
      const body = { ...p,
        segment: p.segment ? Number(p.segment) : null,
        price: Number(p.price), original_price: p.original_price ? Number(p.original_price) : null, stock: Number(p.stock),
        sizes: emptyToArr(p.sizes), colors: emptyToArr(p.colors),
      };
      await api.saveProduct(existing?.slug || '', body);
      onSaved();
    } catch (e) { setErr(e.message); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-product-editor">
      <div className="bg-white rounded-lg max-w-3xl w-full p-6 my-10 relative">
        <button onClick={onClose} className="absolute top-3 right-3"><X className="w-5 h-5" /></button>
        <h2 className="text-2xl font-bold mb-5">{existing ? 'Edit product' : 'New product'}</h2>
        {err && <p className="text-red-600 text-sm mb-3">{err}</p>}
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <label className="col-span-2">Name <input value={p.name} onChange={(e)=>setP({...p,name:e.target.value})} data-testid="pe-name" className="w-full border px-3 py-2 mt-1" /></label>
          <label>Price (INR) <input type="number" value={p.price} onChange={(e)=>setP({...p,price:e.target.value})} data-testid="pe-price" className="w-full border px-3 py-2 mt-1" /></label>
          <label>MRP (shown struck-through) <input type="number" value={p.original_price||''} onChange={(e)=>setP({...p,original_price:e.target.value})} data-testid="pe-original" className="w-full border px-3 py-2 mt-1" /></label>
          <label>Category
            <select value={p.category} onChange={(e)=>setP({...p,category:e.target.value})} data-testid="pe-category" className="w-full border px-3 py-2 mt-1">
              <option value="men">Men</option><option value="women">Women</option><option value="unisex">Unisex</option>
            </select>
          </label>
          <Sel label="Collection" value={p.collection} onChange={(v)=>setP({...p,collection:v})} testid="pe-collection" options={Object.entries(COLLECTION_LABEL)} />
          <Sel label="Price segment" value={p.segment} testid="pe-segment"
            onChange={(v)=>{ const seg = v ? Number(v) : null; setP({...p, segment: seg, ...(seg ? { price: seg, original_price: MRP[seg], offer: SEG_OFFER[seg] || null } : {})}); }}
            options={[['', 'None'], ...SEGMENTS.map((x) => [String(x.price), `₹${x.price}`])]} />
          <Sel label="Offer" value={p.offer} onChange={(v)=>setP({...p,offer:v})} testid="pe-offer" options={[['', 'None'], ['b1g1', 'Buy 1 Get 1 Free'], ['b2g1', 'Buy 2 Get 1 Free']]} />
          <Sel label="Story collection" value={p.theme} onChange={(v)=>setP({...p,theme:v})} testid="pe-theme" options={[['', 'None'], ...Object.entries(THEMES).map(([k, t]) => [k, t.title])]} />
          <Sel label="Product type" value={p.product_type} onChange={(v)=>setP({...p,product_type:v})} testid="pe-type" options={[['', 'None'], ...TYPES]} />
          <label>Sizes (comma-sep) <input value={(p.sizes||[]).join(', ')} onChange={(e)=>setP({...p,sizes:e.target.value})} data-testid="pe-sizes" className="w-full border px-3 py-2 mt-1" /></label>
          <label>Colours (comma-sep) <input value={(p.colors||[]).join(', ')} onChange={(e)=>setP({...p,colors:e.target.value})} data-testid="pe-colors" className="w-full border px-3 py-2 mt-1" /></label>
          <label>Stock <input type="number" value={p.stock} onChange={(e)=>setP({...p,stock:e.target.value})} className="w-full border px-3 py-2 mt-1" /></label>
          <label className="flex items-center gap-2 mt-6 font-semibold"><input type="checkbox" checked={p.in_stock !== false} onChange={(e)=>setP({...p,in_stock:e.target.checked})} data-testid="pe-in-stock" /> In stock (untick to show Out of Stock)</label>
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={p.featured} onChange={(e)=>setP({...p,featured:e.target.checked})} /> Featured</label>
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={p.published} onChange={(e)=>setP({...p,published:e.target.checked})} /> Published</label>
          <label className="col-span-2">Description <textarea value={p.description} onChange={(e)=>setP({...p,description:e.target.value})} data-testid="pe-description" rows={3} className="w-full border px-3 py-2 mt-1" /></label>
          <label className="col-span-2">Story (optional — shown on PDP) <textarea value={p.story} onChange={(e)=>setP({...p,story:e.target.value})} rows={3} className="w-full border px-3 py-2 mt-1" /></label>
          <div className="col-span-2">
            <p className="mb-2">Images</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(p.images||[]).map((src, i) => (
                <div key={i} className="relative">
                  <img src={src.startsWith('http') ? src : `${API}${src}`} alt="" className="w-20 h-20 object-cover border" />
                  <button onClick={() => setP({...p, images: p.images.filter((_,x)=>x!==i)})} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs">×</button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 border cursor-pointer hover:bg-gray-50" data-testid="pe-upload-label">
              <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload image'}
              <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="pe-upload-input" />
            </label>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={save} data-testid="pe-save" className="si-btn si-btn-primary"><Save className="w-4 h-4 inline mr-1" /> Save</button>
          <button onClick={onClose} className="px-5 py-2.5 border text-sm uppercase tracking-wider">Cancel</button>
        </div>
      </div>
    </div>
  );
};


export default ProductEditor;
